import { isNonEmptyString, reduceErrors, validate, validateAll, ValidationErrors } from "@util/validations"
import { NonRefundableAdjustment, totalNonRefundable, validateAdjustment } from "./NonRefundableAdjustment"

export interface RefundableCharge {
  regular: number
  discount: number
  fee: number
  settled: boolean
  stripeRefundable: boolean
}

export interface Cancellation {
  maxRefundable: number
  maxStripeRefundable: number
  netPaid: number
  totalConvenienceFeesFromPayments: number
  totalAchDiscounts: number
  feesAreNonRefundable: boolean
  refundMethod: "stripe" | "manual"
  manualReference: string
  agreeCancellationIsIrreversible: boolean
  nonRefundableAdjustments: NonRefundableAdjustment[]
  refundableCharges: RefundableCharge[]
}

export function cancellationTotal(cancellation: Cancellation): number {
  return cancellation.maxRefundable + cancellation.totalConvenienceFeesFromPayments
}

export function currentNonRefundableFees(cancellation: Cancellation): number {
  return cancellation.feesAreNonRefundable ? cancellation.totalConvenienceFeesFromPayments : 0
}

// Elixir's round/1 rounds halves away from zero. Math.round rounds them toward positive infinity,
// so the two disagree on negative ties: a -37.5 cent discount share is -37 here and -38 there, and
// the form would promise a refund a cent larger than the one the server issues.
function roundHalfAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value)
}

// Mirrors Register.Cancellations.credit_from/2. Bank transfer payments are collected net of the
// tenant's ACH discount while non-refundable amounts are priced off the undiscounted trip cost, so
// the two have to be put in the same units. The rate is not a single number: a refund empties
// charges newest-first, and a passenger can pay part of a trip by card and part by bank transfer,
// so what a slice of returned credit is worth depends on which charge it comes from. Walking the
// charges in the order the refund will consume them prices each slice at its own rate.
//
// Returns what the given charges could not cover alongside the cash: the caller can filter every
// charge out, and that strands the credit rather than freeing it.
function creditFrom(
  cancellation: Cancellation,
  insuranceOnly: boolean,
  charges: RefundableCharge[],
): { cash: number; stranded: number } {
  let nonRefundable = totalNonRefundable(cancellation.nonRefundableAdjustments, insuranceOnly)
  let left = cancellation.netPaid - nonRefundable
  let cash = 0

  for (let charge of charges) {
    let take = Math.min(charge.regular, left)
    if (take <= 0) continue

    cash += take + roundHalfAwayFromZero((charge.discount * take) / charge.regular)
    left -= take
  }

  return { cash: Math.min(cash, cancellation.maxRefundable), stranded: Math.max(left, 0) }
}

// no summary at all falls back to the old mixed-unit subtraction; only a freshly built cancellation
// carries one
function noSummaryCredit(cancellation: Cancellation, insuranceOnly: boolean): { cash: number; stranded: number } {
  let nonRefundable = totalNonRefundable(cancellation.nonRefundableAdjustments, insuranceOnly)

  return { cash: cancellation.maxRefundable - nonRefundable, stranded: 0 }
}

// Mirrors Register.Cancellations.eligible_charges/1: which rail the money goes back on, not whether
// it has landed yet. A payment still settling is credit the passenger is owed either way, and
// pricing it as unreachable is what inflated the discount.
function eligibleCharges(charges: RefundableCharge[], method: Cancellation["refundMethod"]): RefundableCharge[] {
  return method === "stripe" ? charges.filter((charge) => charge.stripeRefundable) : charges
}

// Mirrors Register.Cancellations.refundable_credit/1.
export function refundableCredit(cancellation: Cancellation, insuranceOnly: boolean): number {
  let summary = cancellation.refundableCharges || []

  if (summary.length === 0) return noSummaryCredit(cancellation, insuranceOnly).cash

  return creditFrom(cancellation, insuranceOnly, eligibleCharges(summary, cancellation.refundMethod)).cash
}

export function nonRefundableAchDiscount(cancellation: Cancellation, insuranceOnly: boolean): number {
  let nonRefundable = totalNonRefundable(cancellation.nonRefundableAdjustments, insuranceOnly)

  return cancellation.maxRefundable - refundableCredit(cancellation, insuranceOnly) - nonRefundable
}

export function toBeRefunded(cancellation: Cancellation, insuranceOnly: boolean): number {
  let refundableFees = cancellation.totalConvenienceFeesFromPayments - currentNonRefundableFees(cancellation)

  return Math.max(refundableCredit(cancellation, insuranceOnly) + refundableFees, 0)
}

export function canBeRefundedUsingStripe(cancellation: Cancellation, insuranceOnly: boolean): boolean {
  // Stripe cannot touch a manual payment, one past its refund window, or one still settling. When
  // those hold credit the passenger is owed, an electronic refund would return less than the
  // cancellation calculated, so the whole refund has to go back by hand instead.
  let summary = cancellation.refundableCharges || []

  let { cash, stranded } =
    summary.length === 0
      ? noSummaryCredit(cancellation, insuranceOnly)
      : creditFrom(
          cancellation,
          insuranceOnly,
          summary.filter((charge) => charge.settled && charge.stripeRefundable),
        )

  if (stranded > 0) return false

  let refundable = cash + (cancellation.totalConvenienceFeesFromPayments - currentNonRefundableFees(cancellation))

  if (cancellation.feesAreNonRefundable) {
    return refundable <= cancellation.maxStripeRefundable
  } else {
    return refundable <= cancellation.maxStripeRefundable + cancellation.totalConvenienceFeesFromPayments
  }
}

export function notToBeRefunded(cancellation: Cancellation, insuranceOnly: boolean): number {
  return cancellationTotal(cancellation) - toBeRefunded(cancellation, insuranceOnly)
}

export function validateCancellation(data: Cancellation): ValidationErrors<Cancellation> {
  return reduceErrors({
    agreeCancellationIsIrreversible: [
      validate(
        data.agreeCancellationIsIrreversible,
        "You must acknowledge that cancellations are immediate and irreversible",
      ),
    ],
    manualReference: [
      data.refundMethod === "manual" &&
        validate(isNonEmptyString(data.manualReference), "You must provide a reference for manual refunds"),
    ],
    nonRefundableAdjustments: [
      validateAll(
        data.nonRefundableAdjustments,
        validateAdjustment,
        "All non-refundable adjustments must be positive numbers",
      ),
    ],
  })
}
